// src/App.js
import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Login, { CLIENT_ID, REDIRECT_URI } from './components/Login';
import MainPage from './components/MainPage';
import Playlists from './components/Playlists';
import Podcasts from './components/Podcasts';
import Albums from './components/Albums';
import Artists from './components/Artists';
import { exchangeCodeForToken, refreshAccessToken } from './pkce';
import 'bootstrap/dist/css/bootstrap.min.css';

const App = () => {
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const handleAuth = async () => {
      // Check for authorization code in URL (PKCE flow)
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');

      // Get stored tokens
      let storedToken = window.localStorage.getItem('access_token');
      const storedRefreshToken = window.localStorage.getItem('refresh_token');
      const tokenExpiry = window.localStorage.getItem('token_expiry');

      // If we have a code, exchange it for tokens
      if (code) {
        const codeVerifier = window.localStorage.getItem('code_verifier');

        if (codeVerifier) {
          try {
            const tokenData = await exchangeCodeForToken(code, codeVerifier, CLIENT_ID, REDIRECT_URI);

            // Store tokens
            window.localStorage.setItem('access_token', tokenData.access_token);
            window.localStorage.setItem('refresh_token', tokenData.refresh_token);
            window.localStorage.setItem('token_expiry', Date.now() + (tokenData.expires_in * 1000));
            window.localStorage.removeItem('code_verifier');

            // Clear the URL
            window.history.replaceState({}, document.title, '/');

            setToken(tokenData.access_token);
            setLoading(false);
            return;
          } catch (error) {
            console.error('Failed to exchange code for token:', error);
            window.localStorage.removeItem('code_verifier');
          }
        }

        // Clear the URL even if exchange failed
        window.history.replaceState({}, document.title, '/');
      }

      // Check if existing token is expired
      if (storedToken && tokenExpiry) {
        if (Date.now() >= parseInt(tokenExpiry)) {
          // Token expired, try to refresh
          if (storedRefreshToken) {
            try {
              const tokenData = await refreshAccessToken(storedRefreshToken, CLIENT_ID);

              window.localStorage.setItem('access_token', tokenData.access_token);
              if (tokenData.refresh_token) {
                window.localStorage.setItem('refresh_token', tokenData.refresh_token);
              }
              window.localStorage.setItem('token_expiry', Date.now() + (tokenData.expires_in * 1000));

              storedToken = tokenData.access_token;
            } catch (error) {
              console.error('Failed to refresh token:', error);
              // Clear all tokens on refresh failure
              window.localStorage.removeItem('access_token');
              window.localStorage.removeItem('refresh_token');
              window.localStorage.removeItem('token_expiry');
              storedToken = null;
            }
          } else {
            // No refresh token, clear expired access token
            window.localStorage.removeItem('access_token');
            window.localStorage.removeItem('token_expiry');
            storedToken = null;
          }
        }
      }

      setToken(storedToken);
      setLoading(false);
    };

    handleAuth();
  }, []);

  // Show loading while checking auth
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="spinner-border text-success" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <div className="App">
        <Routes>
          <Route
            path="/"
            element={!token ? <Login /> : <Navigate to="/main" />}
          />
          <Route
            path="/main"
            element={token ? <MainPage token={token} /> : <Navigate to="/" />}
          />
          <Route
            path="/playlists"
            element={token ? <Playlists token={token} /> : <Navigate to="/" />}
          />
          <Route
            path="/podcasts"
            element={token ? <Podcasts token={token} /> : <Navigate to="/" />}
          />
          <Route
            path="/albums"
            element={token ? <Albums token={token} /> : <Navigate to="/" />}
          />
          <Route
            path="/artists"
            element={token ? <Artists token={token} /> : <Navigate to="/" />}
          />
        </Routes>
      </div>
    </Router>
  );
};

export default App;
